import { create } from 'zustand';
import useLoadingStore from './use-loading';
import { makeRequest } from 'api/api-client';
import { HTTP } from 'constants/http-method';
import { API_ENDPOINTS } from 'api/endpoints';

const useEvaluationStore = create((set) => ({
  evalutionsLoading: false,
  totalEvaluations: 0,
  evaluations: [],
  pagination: {
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
  },

  // Function to reset currentPage only when an action happens
  resetCurrentPage: () => {
    set((state) => ({
      pagination: {
        ...state.pagination,
        currentPage: 1,
      },
    }));
  },

  // Function to get evaluations
  getEvaluation: async ({ query = null, successCallback }) => {
    set({ evalutionsLoading: true });
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.GET,
        API_ENDPOINTS.GET_EVALUATION,
        null,
        query
      );

      if (response.success) {
        set({
          totalEvaluations: response.data?.pagination?.totalEvaluations,
          evaluations: response.data?.data || [],
          pagination: {
            pageSize: response.data?.pagination?.limit,
            totalItems: response.data?.pagination?.total,
            currentPage: response.data?.pagination?.page,
            totalPages: response.data?.pagination?.totalPages,
          },
        });
        successCallback && successCallback();
      }
    } catch (error) {
    } finally {
      set({ evalutionsLoading: false });
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to get evaluations by user
  getEvaluationByUserId: async ({ id, query = null, successCallback }) => {
    set({ evalutionsLoading: true });
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.GET,
        API_ENDPOINTS.GET_EVALUATION_BY_USER,
        null,
        query,
        id
      );
      if (response.success) {
        set({
          evaluations: response.data?.data || [],
          pagination: {
            pageSize: response.data?.pagination?.limit,
            totalItems: response.data?.pagination?.total,
            currentPage: response.data?.pagination?.page,
            totalPages: response.data?.pagination?.totalPages,
          },
        });
        successCallback && successCallback();
      }
    } catch (error) {
    } finally {
      set({ evalutionsLoading: false });
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to create evaluation
  createEvaluation: async ({ data, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.POST,
        API_ENDPOINTS.CREATE_EVALUATION,
        data
      );

      if (response.success) {
        await useEvaluationStore.getState().getEvaluation({
          successCallback: () => {
            useEvaluationStore.getState().resetCurrentPage(); // Reset page
            successCallback && successCallback(response);
          },
        });
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to add evaluation answer user
  addEvaluationAnswerUser: async ({ id, data, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.POST,
        API_ENDPOINTS.ADD_EVALUATION_ANSWER_USER,
        data
      );

      if (response.success) {
        set((state) => ({
          evaluations: state.evaluations.map((evaluation) =>
            evaluation.id === id
              ? { ...evaluation, ...response.data?.data }
              : evaluation
          ),
        }));
        successCallback && successCallback(response);
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to delete evaluation
  deleteEvaluation: async ({ id, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.DELETE,
        API_ENDPOINTS.DELETE_EVALUATION,
        null,
        null,
        id
      );

      if (response.success) {
        set((state) => {
          const updatedEvaluations = state.evaluations.filter(
            (evaluation) => evaluation.id !== id
          );

          const updatedTotalItems = updatedEvaluations.length;
          const updatedTotalPages = Math.ceil(
            updatedTotalItems / state.pagination.pageSize
          );

          return {
            evaluations: updatedEvaluations,
            pagination: {
              ...state.pagination,
              totalItems: updatedTotalItems,
              totalPages: updatedTotalPages,
            },
          };
        });

        successCallback && successCallback(response);
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to delete soft competency test
  deleteSoftEvaluation: async ({ id, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.PUT,
        API_ENDPOINTS.SOFT_DELETE_EVALUATION,
        null,
        null,
        id
      );

      if (response.success) {
        set((state) => {
          const updatedEvaluations = state.evaluations.filter(
            (evaluation) => evaluation.id !== id
          );

          const updatedTotalItems = updatedEvaluations.length;
          const updatedTotalPages = Math.ceil(
            updatedTotalItems / state.pagination.pageSize
          );

          return {
            evaluations: updatedEvaluations,
            pagination: {
              ...state.pagination,
              totalItems: updatedTotalItems,
              totalPages: updatedTotalPages,
            },
          };
        });
        successCallback && successCallback(response);
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to update evaluation
  updateEvaluation: async ({ id, data, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.PUT,
        API_ENDPOINTS.UPDATE_EVALUATION,
        data,
        null,
        id
      );

      if (response.success) {
        set((state) => {
          const updatedEvaluation = state.evaluations.find(
            (evaluation) => evaluation.id === id
          );

          if (updatedEvaluation) {
            const updatedEvaluations = [
              { ...updatedEvaluation, ...response.data?.data }, // Updated evaluation
              ...state.evaluations.filter((evaluation) => evaluation.id !== id), // Rest of the evaluations
            ];

            return {
              evaluations: updatedEvaluations,
              pagination: {
                ...state.pagination,
                currentPage: 1, // Reset currentPage to 1
              },
            };
          }

          // If the evaluation is not found, return the state as is
          return state;
        });

        // set((state) => ({
        //   evaluations: state.evaluations.map((evaluation) =>
        //     evaluation.id === id
        //       ? { ...evaluation, ...response.data?.data }
        //       : evaluation
        //   ),
        // }));
        successCallback && successCallback(response);
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to add evaluation question
  createEvaluationQuestion: async ({ data, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.POST,
        API_ENDPOINTS.ADD_EVALUATION_QUESTION,
        data
      );

      if (response.success) {
        // Fetch latest evaluation data after adding a question
        await useEvaluationStore.getState().getEvaluation({
          successCallback: () => {
            successCallback && successCallback();
          },
        });
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to send reminder
  evaluationSendReminder: async ({ id, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.PUT,
        API_ENDPOINTS.EVALUATION_SEND_REMINDER,
        null,
        null,
        id
      );
      if (response.success) {
        successCallback && successCallback(response);
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },
}));

export default useEvaluationStore;
